import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { api } from '../api/nocode';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // 1. Check URL for token (simulating handoff from Hub)
        const params = new URLSearchParams(window.location.search);
        let token = params.get('token');
        
        // Also check hash for token if using HashRouter
        if (!token && window.location.hash.includes('?token=')) {
          const hashParts = window.location.hash.split('?');
          if (hashParts.length > 1) {
            const hashParams = new URLSearchParams(hashParts[1]);
            token = hashParams.get('token');
          }
        }

        if (token) {
          localStorage.setItem('gwa_token', token);
          
          // Clean URL safely without breaking HashRouter
          if (window.location.hash.includes('?token=')) {
            const cleanHash = window.location.hash.split('?')[0]; // retains '#/'
            window.history.replaceState(null, '', window.location.pathname + window.location.search + cleanHash);
          } else if (window.location.search.includes('token=')) {
            const newParams = new URLSearchParams(window.location.search);
            newParams.delete('token');
            const newSearch = newParams.toString() ? '?' + newParams.toString() : '';
            window.history.replaceState(null, '', window.location.pathname + newSearch + window.location.hash);
          }
        } else {
          token = localStorage.getItem('gwa_token');
        }

        // 2. Check for Hardwired Master Admin Token
        if (token === 'master_admin_token') {
          setUser({
            id: 'master-admin',
            name: 'Master Admin',
            email: 'team@gospelwiseauthor.com',
            role: 'admin',
            pass_type: 'allaccess',
            owner_id: 'master-admin'
          });
          setLoading(false);
          return;
        }

        // 3. Process Standard Camper JWT
        if (token) {
          let decoded;
          try {
            decoded = jwtDecode(token);
          } catch (e) {
            throw new Error("Invalid JWT token structure");
          }

          // Safely extract email/name from various JWT payload formats (Supabase, Custom, etc.)
          const searchEmail = decoded.email || decoded.user?.email || decoded.user_metadata?.email;
          const searchName = decoded.name || decoded.user?.name || decoded.user_metadata?.name || 'Camper';
          const searchId = decoded.id || decoded.user?.id || crypto.randomUUID();

          if (!searchEmail) {
            throw new Error("No email found in token payload");
          }

          // Fetch fresh user data from DB.
          // Do NOT rely on NCB's server-side email filter — it misses on case
          // differences and would either return empty (forcing a duplicate INSERT)
          // or, with users[0], risk loading the wrong row. Fetch all and match
          // case-insensitively in JS, the same approach as webhook-receiver.php.
          const res = await api.get('users_fbwm');
          const allUsers = Array.isArray(res) ? res : res.data || [];
          const matchedUser = allUsers.find(
            (u) => (u.email || '').trim().toLowerCase() === searchEmail.trim().toLowerCase()
          );

          if (matchedUser) {
            let resolvedUser = matchedUser;

            // First successful Hub login for this camper — flip first_login_complete.
            // This is the only point in the system where a valid JWT confirms a real
            // login occurred (tripwire webhook and registration both leave it false).
            if (resolvedUser.first_login_complete !== true) {
              try {
                await api.put('users_fbwm', resolvedUser.id, {
                  first_login_complete: true,
                });
                resolvedUser = { ...resolvedUser, first_login_complete: true };
              } catch (flipErr) {
                // Don't block login if this write fails — log and proceed with stale flag.
                console.warn("Failed to flip first_login_complete", flipErr);
              }
            }

            setUser(resolvedUser);
          } else {
            // Auto-provision user if they don't exist yet but have valid JWT
            const payloadToPost = {
              id: searchId,
              name: searchName,
              email: searchEmail,
              role: searchEmail === 'team@gospelwiseauthor.com' ? 'admin' : (decoded.role || 'camper'),
              pass_type: decoded.pass_type || 'free',
              // True at creation — reaching this branch already required a valid Hub JWT,
              // so the login has already happened by the time we provision the row.
              first_login_complete: true,
              owner_id: searchId
            };
            
            const newUserRes = await api.post('users_fbwm', payloadToPost);
            
            // NCB Create endpoints often return { status: 'success', message: 'Created' } rather than the row.
            // We MUST fallback to payloadToPost to ensure name and email are present in local state!
            let newUserData = payloadToPost;
            
            if (newUserRes && newUserRes.data && Array.isArray(newUserRes.data) && newUserRes.data.length > 0) {
              newUserData = newUserRes.data[0];
            } else if (newUserRes && newUserRes.data && typeof newUserRes.data === 'object' && newUserRes.data.email) {
              newUserData = newUserRes.data;
            }
            
            setUser(newUserData);
          }
        }
      } catch (err) {
        console.error("Auth error", err);
        localStorage.removeItem('gwa_token');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Hardwired Admin Login function fallback
  const adminLogin = (email, password) => {
    if (email === 'team@gospelwiseauthor.com' && password === 'ZoBoWriMo8954*') {
      const adminUser = {
        id: 'master-admin',
        name: 'Master Admin',
        email: 'team@gospelwiseauthor.com',
        role: 'admin',
        pass_type: 'allaccess',
        owner_id: 'master-admin'
      };
      setUser(adminUser);
      localStorage.setItem('gwa_token', 'master_admin_token');
      return true;
    }
    return false;
  };

  const logout = () => {
    localStorage.removeItem('gwa_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, adminLogin, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);